import React from 'react';
import { NewModal } from 'lib/components';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { IHighlight } from 'lib/api/highlights/useQueryHighlights';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { useTranslation } from 'react-i18next';

interface UpsertHighlightModalProps {
  highlight?: IHighlight | null;
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  isLoading: boolean;
}

const HighlightSchema = (t: Function) =>
  Yup.object().shape({
    title: Yup.string()
      .min(2, t('Title is too short'))
      .max(255, t('Title is too long'))
      .required(t('Title is required')),
    description: Yup.string()
      .min(10, t('Description is too short'))
      .max(255, t('Description is too long'))
      .required(t('Description is required')),
  });

export const UpsertHighlightModal = ({
  highlight,
  onClose,
  onSave,
  isLoading,
}: UpsertHighlightModalProps) => {
  const { t } = useTranslation();
  const isEditMode = !!highlight;

  const initialValues = {
    title: highlight?.title || '',
    description: highlight?.description || '',
  };

  const headerText = isEditMode ? t('Edit Highlight') : t('Add New Highlight');
  const saveButtonText = isEditMode ? t('Save Changes') : t('Add Highlight');

  return (
    <NewModal
      headerText={headerText}
      closeModal={isLoading ? () => {} : onClose}
      style={{ content: { width: 592, maxWidth: 592 } }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={HighlightSchema(t)}
        onSubmit={values => {
          onSave(values);
        }}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Flex flexDirection='column' gap='10px'>
              <Flex flexDirection='column' gap='11px'>
                <FormikInputField
                  name='title'
                  label={t('Title:')}
                  isRequired={false}
                />
                <FormikTextAreaField
                  name='description'
                  label={t('Highlight description:')}
                  isRequired={false}
                  showCounter
                  maxLength={255}
                />
              </Flex>
              <Gap gap='8px' justifyContent='flex-end'>
                <Button
                  text={t('Discard')}
                  onClick={onClose}
                  variant='secondary'
                  disabled={isLoading || isSubmitting}
                />
                <Button
                  type='submit'
                  text={saveButtonText}
                  variant='primary'
                  loading={isLoading || isSubmitting}
                  disabled={!dirty || !isValid || isLoading || isSubmitting}
                />
              </Gap>
            </Flex>
          </Form>
        )}
      </Formik>
    </NewModal>
  );
};
